// app/javascript/controllers/field_pdf_help_controller.js
//
// Renders a small "?" help affordance next to a charity-care application field label.
// Clicking it opens a popover that shows the ORIGINAL form's PDF page (a png_preview_pages
// image), auto-scrolled + zoomed to the field, with an arrow/highlight pointing at the
// exact spot, plus pan (drag) and zoom (+/- and wheel). A "maximize" button re-opens the
// same viewer in a full-screen modal (and "minimize" restores the popover) for when the
// popover is too small.
//
// It knows nothing about the form model beyond the values wired on the trigger:
//   pages     — [{url, width, height}] for the field's page plus its neighbors (prev/next),
//               so a member can scroll to the adjacent page when a field sits at a page edge.
//   pageIndex — which entry in `pages` is the field's own page.
//   focus     — { left, top, width, height } as 0..1 fractions of the field's page (the label
//               bbox, or a small synthesized box around the input point).
//   label     — the field's display label (used in the popover/modal header).
//
// Register:
//   import FieldPdfHelpController from "./field_pdf_help_controller"
//   application.register("field-pdf-help", FieldPdfHelpController)

import { Controller } from "@hotwired/stimulus";

const MAX_SCALE = 6;
const PAGE_GAP = 8; // gap (stage px) between stacked pages
const MARKER_PAD = 5; // buffer (screen px) around the highlight so it isn't squashed on the text

export default class extends Controller {
  static targets = ["trigger"];
  static values = {
    pages: Array,
    pageIndex: Number,
    focus: Object,
    label: String,
  };

  connect() {
    this.overlay = null; // the popover or modal root we portal to <body>
    this.viewer = null; // { viewport, stage, rect, arrow, scale, tx, ty, ... }
    this._reposition = () => this.positionPopover();
    this._onDocPointer = (e) => this.onDocPointer(e);
    this._onKeydown = (e) => this.onKeydown(e);
    this.docPointerTimer = null; // pending deferred attach of the outside-click listener
    this.injectStylesOnce();
  }

  disconnect() {
    this.close();
  }

  // --- open / close ------------------------------------------------------------

  toggle(event) {
    if (event) event.preventDefault();
    if (this.overlay && this.mode === "popover") {
      this.close();
    } else {
      this.open("popover");
    }
  }

  maximize(event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    this.open("modal");
  }

  minimize(event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    this.open("popover");
  }

  open(mode) {
    this.close();
    this.mode = mode;
    this.overlay = mode === "modal" ? this.buildModal() : this.buildPopover();
    document.body.appendChild(this.overlay);

    if (mode === "popover") {
      this.positionPopover();
      window.addEventListener("scroll", this._reposition, true);
      window.addEventListener("resize", this._reposition);
      // Defer so the click that opened us doesn't immediately close us. Tracked so close()
      // can cancel it — otherwise a rapid open→close/maximize could attach the listener after
      // teardown (a dangling document listener).
      this.docPointerTimer = setTimeout(() => {
        this.docPointerTimer = null;
        document.addEventListener("pointerdown", this._onDocPointer, true);
      }, 0);
    }
    document.addEventListener("keydown", this._onKeydown, true);
    this.initViewer();
  }

  close() {
    if (this.docPointerTimer) { clearTimeout(this.docPointerTimer); this.docPointerTimer = null; }
    window.removeEventListener("scroll", this._reposition, true);
    window.removeEventListener("resize", this._reposition);
    document.removeEventListener("pointerdown", this._onDocPointer, true);
    document.removeEventListener("keydown", this._onKeydown, true);
    if (this.overlay) { this.overlay.remove(); this.overlay = null; }
    this.viewer = null;
    this.mode = null;
  }

  onDocPointer(e) {
    if (!this.overlay) return;
    if (this.overlay.contains(e.target)) return;
    if (this.hasTriggerTarget && this.triggerTarget.contains(e.target)) return;
    this.close();
  }

  onKeydown(e) {
    if (e.key === "Escape") { this.close(); }
  }

  // --- DOM building ------------------------------------------------------------

  buildPopover() {
    const root = this.el("div", "fpdf-popover");
    root.appendChild(this.buildHeader());
    root.appendChild(this.buildViewport());
    return root;
  }

  buildModal() {
    const backdrop = this.el("div", "fpdf-backdrop");
    backdrop.addEventListener("pointerdown", (e) => { if (e.target === backdrop) this.close(); });
    const card = this.el("div", "fpdf-modal-card");
    card.appendChild(this.buildHeader());
    card.appendChild(this.buildViewport(true));
    backdrop.appendChild(card);
    return backdrop;
  }

  buildHeader() {
    const header = this.el("div", "fpdf-header");
    const title = this.el("div", "fpdf-title");
    title.textContent = this.labelValue ? `On the form: ${this.labelValue}` : "On the original form";
    header.appendChild(title);

    const controls = this.el("div", "fpdf-controls");
    controls.appendChild(this.iconButton("−", "Zoom out", () => this.zoomBy(1 / 1.3)));
    controls.appendChild(this.iconButton("+", "Zoom in", () => this.zoomBy(1.3)));
    if (this.mode === "modal") {
      controls.appendChild(this.iconButton(this.minimizeGlyph(), "Minimize", (e) => this.minimize(e)));
    } else {
      controls.appendChild(this.iconButton(this.expandGlyph(), "Expand", (e) => this.maximize(e)));
    }
    controls.appendChild(this.iconButton(this.closeGlyph(), "Close", () => this.close()));
    header.appendChild(controls);
    return header;
  }

  buildViewport(isModal = false) {
    const wrap = this.el("div", isModal ? "fpdf-viewport fpdf-viewport--modal" : "fpdf-viewport");
    const stage = this.el("div", "fpdf-stage");

    // Stack the page images vertically; the browser caches by URL so this is cheap.
    this.pagesValue.forEach((page, idx) => {
      const img = new Image();
      img.src = page.url;
      img.draggable = false;
      img.alt = "";
      img.className = "fpdf-img";
      img.style.width = `${page.width}px`;
      img.style.height = `${page.height}px`;
      if (idx < this.pagesValue.length - 1) img.style.marginBottom = `${PAGE_GAP}px`;
      stage.appendChild(img);
    });

    const overlay = this.el("div", "fpdf-overlay");
    const rect = this.el("div", "fpdf-rect");
    const arrow = this.el("div", "fpdf-arrow");
    overlay.appendChild(rect);
    overlay.appendChild(arrow);

    wrap.appendChild(stage);
    wrap.appendChild(overlay);

    this._pending = { viewport: wrap, stage, rect, arrow };
    return wrap;
  }

  // --- geometry ----------------------------------------------------------------

  // Cumulative layout of the stacked pages, plus where the field's page sits within it.
  geometry() {
    const pages = this.pagesValue;
    const totalW = Math.max(...pages.map((p) => p.width));
    let totalH = 0;
    let focusOffsetY = 0;
    pages.forEach((p, idx) => {
      if (idx === this.pageIndexValue) focusOffsetY = totalH;
      totalH += p.height + (idx < pages.length - 1 ? PAGE_GAP : 0);
    });
    const focusPage = pages[this.pageIndexValue];
    return { totalW, totalH, focusOffsetY, pageW: focusPage.width, pageH: focusPage.height };
  }

  focusBox() {
    const f = this.focusValue || {};
    return {
      left: Number(f.left) || 0,
      top: Number(f.top) || 0,
      width: Number(f.width) || 0.06,
      height: Number(f.height) || 0.02,
    };
  }

  // --- viewer (pan/zoom) -------------------------------------------------------

  initViewer() {
    const { viewport, stage, rect, arrow } = this._pending;
    const vpRect = viewport.getBoundingClientRect();
    const g = this.geometry();
    const v = {
      viewport, stage, rect, arrow, geo: g,
      vpW: vpRect.width,
      vpH: vpRect.height,
      scale: 1, tx: 0, ty: 0,
    };
    this.viewer = v;

    v.minScale = Math.min(v.vpW / g.totalW, v.vpH / g.totalH);

    // Initial zoom: show a window ~this fraction of the page's width around the field.
    const fraction = this.mode === "modal" ? 0.55 : 0.42;
    const initial = v.vpW / (g.pageW * fraction);
    v.scale = Math.max(v.minScale, Math.min(MAX_SCALE, initial));

    // Center the focus point in the viewport.
    const f = this.focusBox();
    const fx = (f.left + f.width / 2) * g.pageW;
    const fy = g.focusOffsetY + (f.top + f.height / 2) * g.pageH;
    v.tx = v.vpW / 2 - fx * v.scale;
    v.ty = v.vpH / 2 - fy * v.scale;
    this.clamp();
    this.applyTransform();

    // Pan
    let dragging = false, sx = 0, sy = 0, stx = 0, sty = 0;
    viewport.addEventListener("pointerdown", (e) => {
      dragging = true; sx = e.clientX; sy = e.clientY; stx = v.tx; sty = v.ty;
      viewport.setPointerCapture(e.pointerId);
      viewport.classList.add("is-grabbing");
    });
    viewport.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      v.tx = stx + (e.clientX - sx);
      v.ty = sty + (e.clientY - sy);
      this.clamp();
      this.applyTransform();
    });
    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      try { viewport.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      viewport.classList.remove("is-grabbing");
    };
    viewport.addEventListener("pointerup", endDrag);
    viewport.addEventListener("pointercancel", endDrag);

    // Wheel zoom around cursor
    viewport.addEventListener("wheel", (e) => {
      e.preventDefault();
      const r = viewport.getBoundingClientRect();
      this.zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
  }

  clamp() {
    const v = this.viewer;
    const sW = v.geo.totalW * v.scale;
    const sH = v.geo.totalH * v.scale;
    v.tx = sW <= v.vpW ? (v.vpW - sW) / 2 : Math.min(0, Math.max(v.vpW - sW, v.tx));
    v.ty = sH <= v.vpH ? (v.vpH - sH) / 2 : Math.min(0, Math.max(v.vpH - sH, v.ty));
  }

  zoomBy(factor, cx, cy) {
    const v = this.viewer;
    if (!v) return;
    if (cx == null) cx = v.vpW / 2;
    if (cy == null) cy = v.vpH / 2;
    const newScale = Math.max(v.minScale, Math.min(MAX_SCALE, v.scale * factor));
    const stageX = (cx - v.tx) / v.scale;
    const stageY = (cy - v.ty) / v.scale;
    v.scale = newScale;
    v.tx = cx - stageX * v.scale;
    v.ty = cy - stageY * v.scale;
    this.clamp();
    this.applyTransform();
  }

  applyTransform() {
    const v = this.viewer;
    v.stage.style.transform = `translate(${v.tx}px, ${v.ty}px) scale(${v.scale})`;
    this.renderMarker();
  }

  // Marker (highlight rect + arrow) lives in the un-scaled overlay layer, so it stays crisp;
  // we recompute its viewport-space position on every transform. A small pad keeps the box off
  // the label text.
  renderMarker() {
    const v = this.viewer;
    const g = v.geo;
    const f = this.focusBox();
    const left = v.tx + f.left * g.pageW * v.scale - MARKER_PAD;
    const top = v.ty + (g.focusOffsetY + f.top * g.pageH) * v.scale - MARKER_PAD;
    const w = f.width * g.pageW * v.scale + MARKER_PAD * 2;
    const h = f.height * g.pageH * v.scale + MARKER_PAD * 2;

    v.rect.style.left = `${left}px`;
    v.rect.style.top = `${top}px`;
    v.rect.style.width = `${w}px`;
    v.rect.style.height = `${h}px`;

    // Arrow bobs just above the highlight, tip pointing down at its top edge.
    v.arrow.style.left = `${left + w / 2}px`;
    v.arrow.style.top = `${top}px`;
  }

  // --- positioning -------------------------------------------------------------

  positionPopover() {
    if (!this.overlay || this.mode !== "popover" || !this.hasTriggerTarget) return;
    if (window.innerWidth <= 640) {
      this.overlay.style.top = "";
      this.overlay.style.left = "";
      return;
    }
    const r = this.triggerTarget.getBoundingClientRect();
    const pw = this.overlay.offsetWidth;
    const ph = this.overlay.offsetHeight;
    const M = 8, GAP = 8;
    let top = r.bottom + GAP;
    if (top + ph > window.innerHeight - M) {
      top = Math.max(M, r.top - GAP - ph);
    }
    let left = r.left + r.width / 2 - pw / 2;
    left = Math.max(M, Math.min(left, window.innerWidth - pw - M));
    this.overlay.style.top = `${Math.round(top)}px`;
    this.overlay.style.left = `${Math.round(left)}px`;
  }

  // --- small helpers -----------------------------------------------------------

  el(tag, className, html) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  iconButton(glyph, label, onClick) {
    const b = this.el("button", "fpdf-btn");
    b.type = "button";
    b.setAttribute("aria-label", label);
    b.title = label;
    b.innerHTML = glyph;
    b.addEventListener("click", onClick);
    return b;
  }

  expandGlyph() {
    return '<svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M216,48V96a8,8,0,0,1-16,0V67.31l-50.34,50.35a8,8,0,0,1-11.32-11.32L188.69,56H160a8,8,0,0,1,0-16h48A8,8,0,0,1,216,48ZM106.34,138.34,56,188.69V160a8,8,0,0,0-16,0v48a8,8,0,0,0,8,8H96a8,8,0,0,0,0-16H67.31l50.35-50.34a8,8,0,0,0-11.32-11.32Z"></path></svg>';
  }

  minimizeGlyph() {
    return '<svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,53.66,163.31,104H192a8,8,0,0,1,0,16H144a8,8,0,0,1-8-8V64a8,8,0,0,1,16,0V92.69l50.34-50.35a8,8,0,0,1,11.32,11.32ZM112,136H64a8,8,0,0,0,0,16H92.69L42.34,202.34a8,8,0,0,0,11.32,11.32L104,163.31V192a8,8,0,0,0,16,0V144A8,8,0,0,0,112,136Z"></path></svg>';
  }

  closeGlyph() {
    return '<svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
  }

  injectStylesOnce() {
    if (document.getElementById("fpdf-styles")) return;
    const style = document.createElement("style");
    style.id = "fpdf-styles";
    style.textContent = `
      .fpdf-popover{position:fixed;z-index:9998;width:360px;max-width:calc(100vw - 16px);
        background:#fff;border:1px solid #ECEDF2;border-radius:16px;overflow:hidden;
        box-shadow:0 12px 34px rgba(45,48,71,.18);display:flex;flex-direction:column;}
      .fpdf-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(16,17,26,.55);
        display:flex;align-items:center;justify-content:center;padding:16px;}
      .fpdf-modal-card{background:#fff;border-radius:16px;overflow:hidden;display:flex;flex-direction:column;
        width:min(900px,92vw);max-height:88vh;box-shadow:0 20px 60px rgba(0,0,0,.4);}
      .fpdf-header{display:flex;align-items:center;gap:8px;padding:10px 12px;
        border-bottom:1px solid #ECEDF2;background:#fff;}
      .fpdf-title{font-size:13px;font-weight:600;color:#2D3047;flex:1;min-width:0;
        white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
      .fpdf-controls{display:flex;align-items:center;gap:4px;flex-shrink:0;}
      .fpdf-btn{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;
        border-radius:7px;border:1px solid #ECEDF2;background:#fff;color:#5A5E78;cursor:pointer;
        font-size:16px;line-height:1;padding:0;}
      .fpdf-btn:hover{background:#F5F3FF;color:#6D28D9;border-color:#DDD6FE;}
      .fpdf-viewport{position:relative;overflow:hidden;height:260px;background:#F3F4F8;
        touch-action:none;cursor:grab;}
      .fpdf-viewport.is-grabbing{cursor:grabbing;}
      .fpdf-viewport--modal{height:min(70vh,640px);}
      .fpdf-stage{position:absolute;top:0;left:0;transform-origin:0 0;will-change:transform;}
      .fpdf-img{display:block;max-width:none;user-select:none;-webkit-user-drag:none;}
      .fpdf-overlay{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
      .fpdf-rect{position:absolute;border:2px solid #7C3AED;border-radius:6px;
        background:rgba(124,58,237,.12);box-shadow:0 0 0 9999px rgba(16,17,26,.04);
        animation:fpdf-pulse 1.6s ease-in-out infinite;}
      .fpdf-arrow{position:absolute;width:0;height:0;margin-left:-9px;margin-top:-16px;
        border-left:9px solid transparent;border-right:9px solid transparent;
        border-top:14px solid #7C3AED;filter:drop-shadow(0 1px 2px rgba(0,0,0,.3));
        animation:fpdf-bob 1s ease-in-out infinite;}

      @keyframes fpdf-pulse{0%,100%{border-color:#7C3AED;}50%{border-color:#C4B5FD;}}
      @keyframes fpdf-bob{0%,100%{transform:translateY(0);}50%{transform:translateY(-4px);}}
      @media (max-width: 640px){
        .fpdf-popover{position:fixed;bottom:0;left:0;right:0;top:auto;width:100%;max-width:100%;
          border-radius:16px 16px 0 0;box-shadow:0 -4px 24px rgba(45,48,71,.18);}
      }
      @media (prefers-reduced-motion: reduce){
        .fpdf-rect,.fpdf-arrow{animation:none;}
      }
    `;
    document.head.appendChild(style);
  }
}
